import Action from '../action'
import {
  completedQuery,
  deprioritizedQueries,
  invalidatedQueries,
  invalidatedQueriesByName,
  loadMore,
  prioritizedQueries,
} from '../actions/graphql'
import Future from 'src/utils/Future'
import { objectSize } from 'src/helpers'
import {
  deepmergeOptions,
  denormalizeValue,
  gatherNormalizedData,
  normalizeValue,
} from 'src/normalized-data/normalization'
import deepmerge from 'deepmerge'
import {
  Changes,
  GraphQLObject,
  QueryProperties,
} from 'src/legacy_graphql/Query'
import { NormalizedData, Operation } from 'src/legacy_graphql'
import ObjectMap from 'src/utils/ObjectMap'
import { prettyPrintOperation } from 'src/legacy_graphql/utils'

type GraphQLState = {
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  queryStates: ObjectMap<Operation<unknown, unknown>, Future<any>>
  queryProperties: ObjectMap<
    Operation<unknown, unknown>,
    QueryProperties<GraphQLObject, unknown, unknown>
  >
  queryRequests: ObjectMap<
    Operation<unknown, unknown>,
    ObjectMap<{ offset?: number | null; limit?: number | null }, number>
  >
  optimisticMutations: {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    operation: Operation<any, unknown>
    failureMessage: string
    revert: Changes
  }[]
  blockingMutationTransitionMessage?: string
  normalizedData: NormalizedData
}

const id = <T>(value: T) => value
const empty = () => []

const initialState: GraphQLState = {
  queryStates: ObjectMap(Future(), prettyPrintOperation),
  queryProperties: ObjectMap(
    {
      mapResult: id,
      updateState: id,
      loadMore: id,
      mapState: id,
      additionalPagesToLoad: empty,
    },
    prettyPrintOperation,
  ),
  queryRequests: ObjectMap(ObjectMap(0), prettyPrintOperation),
  optimisticMutations: [],
  normalizedData: NormalizedData(),
}

const queriesReducer = (state = initialState, action: Action): GraphQLState => {
  switch (action.type) {
    case prioritizedQueries.type:
    case deprioritizedQueries.type:
      const queries =
        action.type === prioritizedQueries.type
          ? action.queries
          : action.queries.filter(query =>
              state.queryProperties.contains(query.operation),
            )
      return {
        ...state,
        queryProperties: queries.reduce(
          (
            queryProperties,
            {
              operation,
              mapResult,
              updateState,
              loadMore,
              mapState,
              additionalPagesToLoad,
              hitCDNCache,
            },
          ) =>
            queryProperties.setIfNotPresent(operation, {
              mapResult,
              updateState,
              loadMore,
              mapState,
              additionalPagesToLoad,
              hitCDNCache,
            }),
          state.queryProperties,
        ),
        queryRequests: queries.reduce(
          (requests, { operation, offset, limit }) =>
            requests.update(operation, requests =>
              requests.update({ offset, limit }, requests =>
                action.type === prioritizedQueries.type
                  ? requests + action.increment
                  : requests - action.decrement,
              ),
            ),
          state.queryRequests,
        ),
      }
    case invalidatedQueries.type:
      return {
        ...state,
        queryStates: action.queries.reduce(
          (queryStates, { operation }) => queryStates.set(operation, Future()),
          state.queryStates,
        ),
      }
    case invalidatedQueriesByName.type:
      const queryNameRegex = /query ([A-Za-z]*)/
      return {
        ...state,
        queryStates: state.queryStates
          .entries()
          .reduce((queryStates, [operation, _future]) => {
            const match = operation.query.match(queryNameRegex)
            if (match && action.queryNames.includes(match[1])) {
              return queryStates.set(operation, Future())
            } else {
              return queryStates
            }
          }, state.queryStates),
      }
    case loadMore.type:
      return {
        ...state,
        queryStates: state.queryStates.update(action.operation, value =>
          value.map(state.queryProperties.get(action.operation).loadMore),
        ),
      }
    case completedQuery.type:
      return {
        ...state,
        queryStates: state.queryStates.update(action.operation, queryState =>
          Future(
            state.queryProperties
              .get(action.operation)
              .mapResult(
                action.result,
                action.offset,
                action.limit,
                queryState.value !== undefined
                  ? denormalizeValue(queryState.value, state.normalizedData)
                  : undefined,
              )
              .map(normalizeValue),
          ),
        ),
      }
    default:
      return state
  }
}

// This is only currently neccessary when converting sticker assets from soc-editor hosted images
// into soc-backend. In the future if you need to block cache updates you can add this flag to the
// action you need to block or all action types (if necessary)
const getOrBlockNormalizedData = (action: Action) =>
  gatherNormalizedData(action)

const graphql = (state = initialState, action: Action): GraphQLState => {
  const newNormalizedData = getOrBlockNormalizedData(action)

  if (objectSize(newNormalizedData) === 0) {
    return queriesReducer(state, action)
  }

  const normalizedState = {
    ...state,
    normalizedData: deepmerge(
      state.normalizedData,
      newNormalizedData,
      deepmergeOptions,
    ),
  }

  const updatedState = queriesReducer(normalizedState, action)

  return {
    ...updatedState,
    queryStates: updatedState.queryStates,
  }
}

export default graphql
